"use client";
// skill-ack: ui-page repo skill file is not registered in this repo
// (frontend/.claude/skills/ui-page/SKILL.md does not exist, only the root
// router) -- generalizing ReferralAddressField.tsx verbatim, same
// @/components/ui imports, no new primitives, no restyled primitives, no new
// visual design.

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Controller, useFormContext } from "react-hook-form";
import { CheckIcon } from "@/components/ui/CheckIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { XIcon } from "@/components/ui/XIcon";
import { cn } from "@/lib/utils";

/** Statuses shared by both the referrer and parent field validators. */
type AddressFieldStatus =
  | "idle"
  | "checking"
  | "valid"
  | "invalid"
  | "self"
  | "not-member"
  | "full"
  | "unreachable";

const FAILED: AddressFieldStatus[] = [
  "invalid",
  "self",
  "not-member",
  "full",
  "unreachable",
];

interface AddressFieldProps {
  /** RHF field name holding the address. */
  name: string;
  /** Already-translated field label. */
  label: string;
  status: AddressFieldStatus;
  /** Full `checkout.*` message key for the current status, or undefined for none. */
  messageKey: string | undefined;
  disabled?: boolean;
}

/**
 * Address input with live on-chain validation. Shared by the referrer and
 * parent fields on checkout — they validate different rules (see
 * useReferrerFieldValidation vs useParentFieldValidation) but render
 * identically: type, get a spinner, then a check or a cross plus a reason.
 *
 * Deliberately no "submit" button — it made users think the address had been
 * accepted when all it did was run a regex.
 */
export const AddressField = ({
  name,
  label,
  status,
  messageKey,
  disabled,
}: AddressFieldProps) => {
  const t = useTranslations();
  const { control } = useFormContext();

  const hasFailed = FAILED.includes(status);

  const adornment = (
    <div className="flex items-center justify-center size-5">
      <AnimatePresence mode="wait" initial={false}>
        {status === "checking" && (
          <motion.div
            key="spinner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <Spinner className="size-5 text-text-secondary" />
          </motion.div>
        )}
        {status === "valid" && (
          <motion.div
            key="check"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <CheckIcon size={20} className="text-semantic-success" />
          </motion.div>
        )}
        {hasFailed && (
          <motion.div
            key="x"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <XIcon size={20} className="text-semantic-error" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="space-y-2 w-full">
          <Label htmlFor={name}>{label}</Label>
          <Input
            {...field}
            value={field.value ?? ""}
            id={name}
            type="text"
            dir="ltr"
            placeholder="0x..."
            autoComplete="off"
            spellCheck={false}
            disabled={disabled}
            className={cn({
              "border-semantic-error": hasFailed,
              "border-semantic-success": status === "valid",
            })}
            endAdornment={adornment}
          />
          {messageKey && status !== "idle" ? (
            <p
              className={cn("text-xs leading-tight ms-1.5", {
                "text-semantic-error": hasFailed,
                "text-semantic-success": status === "valid",
                "text-text-secondary": status === "checking",
              })}
            >
              {t(messageKey)}
            </p>
          ) : null}
        </div>
      )}
    />
  );
};
